import {
  auditPageSchema,
  legalDocumentViewSchema,
  operationListItemSchema,
  operationPageSchema,
  storageBoxViewSchema,
  type AuditPage,
  type LegalDocumentKey,
  type LegalDocumentView,
  type OperationListItem,
  type OperationPage,
  type OperationStatus,
  type OperationType,
  type StorageBoxView,
} from '@kraaapf/contracts';
import { z } from 'zod';

import type { PortalClient } from '../http-client';

/**
 * Die Endpunkte, die es erst mit dem Adminportal gibt.
 *
 * Sie liegen unter `/admin/*`. Die bestehenden (`/fleet`, `/sites`,
 * `/storage-boxes`) behalten ihre Pfade: Sie stehen in jedem Runbook und im
 * Abnahmeskript. Die Grenze zwischen Betreiber und Kunde ist der Guard und
 * nicht der Pfad.
 */
function buildAdminApi(client: PortalClient) {
  return {
    /**
     * Operationen über die ganze Flotte hinweg.
     *
     * Die Startseite der Fehlersuche: „Hängt gerade irgendwo etwas?" ließ sich
     * bis M7 nur beantworten, indem man jede Site einzeln abfragte.
     */
    async operations(
      query: {
        status?: OperationStatus;
        type?: OperationType;
        siteId?: string;
        nodeId?: string;
        cursor?: string;
        limit?: number;
      } = {},
    ): Promise<OperationPage> {
      return client.get('/admin/operations', operationPageSchema, { query });
    },

    async operation(operationId: string): Promise<OperationListItem> {
      return client.get(`/admin/operations/${operationId}`, operationListItemSchema);
    },

    /**
     * Das Prüfprotokoll, seitenweise.
     *
     * Geblättert wird über einen Zeiger und nicht über eine Seitenzahl: In ein
     * Protokoll wird ständig vorne geschrieben, und mit einem Versatz verschöbe
     * sich der Ausschnitt zwischen zwei Abrufen.
     */
    async audit(
      query: {
        action?: string;
        actorId?: string;
        targetType?: string;
        targetId?: string;
        cursor?: string;
        limit?: number;
      } = {},
    ): Promise<AuditPage> {
      return client.get('/admin/audit', auditPageSchema, { query });
    },

    // -- Rechtstexte ---------------------------------------------------------

    async legalDocuments(): Promise<LegalDocumentView[]> {
      const response = await client.get(
        '/admin/legal',
        z.object({ documents: z.array(legalDocumentViewSchema) }),
      );

      return response.documents;
    },

    async legalVersions(key: LegalDocumentKey): Promise<LegalDocumentView[]> {
      const response = await client.get(
        `/admin/legal/${key}`,
        z.object({ versions: z.array(legalDocumentViewSchema) }),
      );

      return response.versions;
    },

    /**
     * Veröffentlicht eine neue Fassung.
     *
     * Es gibt kein Ändern und kein Löschen: Eine Zustimmung bezieht sich auf
     * eine bestimmte Nummer, und ein überschriebener Text machte sie
     * unbelegbar.
     */
    async publishLegal(
      key: LegalDocumentKey,
      input: { title: string; bodyMarkdown: string },
    ): Promise<LegalDocumentView> {
      return client.post(`/admin/legal/${key}`, legalDocumentViewSchema, input);
    },

    // -- Storage Boxen -------------------------------------------------------

    async storageBoxes(): Promise<StorageBoxView[]> {
      const response = await client.get(
        '/storage-boxes',
        z.object({ boxes: z.array(storageBoxViewSchema) }),
      );

      return response.boxes;
    },

    async createStorageBox(input: {
      label: string;
      host: string;
      port?: number;
      username: string;
      basePath: string;
      capacityGb: number;
      usedGb?: number;
      sshPrivateKey: string;
      sshHostKey: string;
      enabled?: boolean;
    }): Promise<StorageBoxView> {
      return client.post('/storage-boxes', storageBoxViewSchema, input);
    },

    /**
     * Ändert eine Box.
     *
     * Es gibt kein Löschen: Eine Box zu entfernen, auf der Repositories liegen,
     * machte die Sicherungen dieser Sites unauffindbar. Der vorgesehene Weg ist
     * `enabled: false` — sie nimmt keine neuen Sites mehr auf, versorgt die
     * bestehenden aber weiter.
     */
    async updateStorageBox(
      boxId: string,
      changes: { enabled?: boolean; capacityGb?: number; usedGb?: number },
    ): Promise<StorageBoxView> {
      return client.patch(`/storage-boxes/${boxId}`, storageBoxViewSchema, changes);
    },
  };
}

/** Die öffentliche Form dieses Bereichs — abgeleitet, damit sie nicht zweimal dasteht. */
export type AdminApi = ReturnType<typeof buildAdminApi>;

export function createAdminApi(client: PortalClient): AdminApi {
  return buildAdminApi(client);
}
